import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import {
    ApplicationContextStore,
    NotificationService,
    PushNotificationEventsService,
    RouteSegments,
    RouterService,
    SignalRMethods,
    SignalRService,
    ToastService,
} from '@raptor-fe/services';
import { pipe, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { Notification, NotificationStatus } from '@raptor-fe/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';

/**
 * Resolves the screen a tapped push should open, or null to stay put.
 *
 * Returns null when the payload cannot satisfy the destination's parameters, which is what keeps
 * notifications delivered before those parameters were sent from opening a screen that cannot load.
 */
export function resolvePushRoute(
    data: Record<string, string | undefined>,
): { path: string; parameters: object } | null {
    const deepLink = data['deepLink'];

    if (!deepLink) {
        return null;
    }

    if (deepLink === RouteSegments.INSPECTION_DETAILS) {
        const { inspectionId, tenantId, branchId } = data;

        return inspectionId && tenantId && branchId
            ? { path: deepLink, parameters: { id: inspectionId, tenantId, branchId } }
            : null;
    }

    return deepLink === RouteSegments.TECH_WORK ? { path: deepLink, parameters: {} } : null;
}

export interface NotificationStoreState {
    tenantId: string;
    results: Notification[];
    loading: boolean;
}

export const NotificationStore = signalStore(
    { providedIn: 'root' },
    withState<NotificationStoreState>({
        tenantId: '',
        results: [],
        loading: false,
    }),
    withProps((store) => ({
        notificationCount: () => store.results().length,
    })),
    withComputed((store) => ({
        /**
         * Count of notifications still in the New (unread) state. Surfaced to the My Work toolbar
         * bell badge and the notifications page mark-all-read affordance.
         */
        unreadCount: computed(() => store.results().filter((n) => n.status === NotificationStatus.New).length),
    })),
    withMethods((store, service = inject(NotificationService), toastService = inject(ToastService)) => ({
        // Fetch Notifications
        fetchNotifications: rxMethod<void>(
            pipe(
                tap(() => patchState(store, { loading: true })),
                switchMap(() =>
                    service.getByUser(store.tenantId()).pipe(
                        tapResponse({
                            next: (notifications) => {
                                patchState(store, {
                                    results: notifications,
                                });
                            },
                            error: async () => {
                                await toastService.showError('Error fetching tenants');
                            },
                            finalize: () => patchState(store, { loading: false }),
                        }),
                    ),
                ),
            ),
        ),
        // Mark Notification as Dismissed
        markAsDismissed: rxMethod<{ notificationId: string }>(
            pipe(
                switchMap((payload) =>
                    service.markAsRead(store.tenantId(), payload.notificationId, NotificationStatus.Dismissed).pipe(
                        tapResponse({
                            next: (updatedNotification) => {
                                const updatedResults = store.results().filter((n) => n.id !== updatedNotification.id);
                                patchState(store, { results: updatedResults });
                            },
                            error: async () => {
                                await toastService.showError('Error marking notification as dismissed');
                            },
                        }),
                    ),
                ),
            ),
        ),
        /**
         * Marks every unread notification as read in one request. On success the in-memory results
         * are transitioned to {@link NotificationStatus.Read} so the derived `unreadCount` recomputes
         * to zero without a refetch round trip. The bare count returned by the endpoint is not
         * surfaced to state; the optimistic in-memory transition is the source of truth for the badge.
         */
        markAllRead: rxMethod<void>(
            pipe(
                switchMap(() =>
                    service.markAllRead(store.tenantId()).pipe(
                        tapResponse({
                            next: () => {
                                const readResults = store
                                    .results()
                                    .map((n) => (n.status === NotificationStatus.New ? { ...n, status: NotificationStatus.Read } : n));
                                patchState(store, { results: readResults });
                            },
                            error: async () => {
                                await toastService.showError('Error marking notifications as read');
                            },
                        }),
                    ),
                ),
            ),
        ),
    })),
    withHooks(
        (
            store,
            appContext = inject(ApplicationContextStore),
            toastService = inject(ToastService),
            signalRService = inject(SignalRService),
            pushEvents = inject(PushNotificationEventsService),
            routerService = inject(RouterService),
        ) => {
            const destroy$ = new Subject<void>();
            return {
                onInit: () => {
                    patchState(store, { tenantId: appContext.tenantId() });

                    if (appContext.isLoggedIn() === false) {
                        return;
                    }

                    signalRService.startConnection();
                    signalRService.onNotification(SignalRMethods.NOTIFICATION, async (data) => {
                        const notification = data as Notification;
                        const currentResults = store.results();

                        const existingIndex = currentResults.findIndex((n) => n.id === notification.id);
                        if (existingIndex !== -1) {
                            currentResults.splice(existingIndex, 1);
                        }

                        patchState(store, { results: [notification, ...currentResults] });
                        await toastService.displayToast(notification.title + ' ' + notification.message, 'info');
                    });

                    pushEvents.received$.pipe(takeUntil(destroy$)).subscribe(async (notification) => {
                        const title = notification.title ?? '';
                        const body = notification.body ?? '';
                        const message = [title, body].filter((part) => part.length > 0).join(' ');
                        if (message.length > 0) {
                            await toastService.displayToast(message, 'info');
                        }
                        store.fetchNotifications();
                    });

                    pushEvents.actionPerformed$.pipe(takeUntil(destroy$)).subscribe(async (action) => {
                        store.fetchNotifications();

                        const data = (action.notification?.data ?? {}) as Record<string, string | undefined>;
                        const route = resolvePushRoute(data);

                        if (route !== null) {
                            await routerService.navigateTo(route);
                        }
                    });
                },
                onDestroy: () => {
                    destroy$.next();
                    destroy$.complete();
                    signalRService.stopConnection();
                },
            };
        },
    ),
);
